import { useMemo, useState } from 'react';
import { ChevronDown, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from './command';
import { cn } from '../../lib/utils';

export interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectProps {
  label: string;
  options: MultiSelectOption[];
  selected: string[];
  onChange: (next: string[]) => void;
  /** Search box inside the dropdown. Defaults on once the list stops being
   *  scannable at a glance — Manufacturer has ~200 values, Vendor has ~6. */
  searchable?: boolean;
  searchPlaceholder?: string;
  /** Shown in place of tags when nothing is selected. */
  placeholder?: string;
  className?: string;
}

const SEARCHABLE_THRESHOLD = 8;

/**
 * The single filter control for every table (BOT-392).
 *
 * Replaces the old mix of capsule-button rows and bare `<select>`s that the
 * Confluence review flagged as inconsistent in size, colour and behaviour.
 * Closed by default, opens to a (optionally searchable) checkbox list, and
 * shows selections as removable tags inline in the trigger.
 */
export function MultiSelect({
  label,
  options,
  selected,
  onChange,
  searchable,
  searchPlaceholder,
  placeholder = 'All',
  className,
}: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const showSearch = searchable ?? options.length > SEARCHABLE_THRESHOLD;

  const labelByValue = useMemo(
    () => new Map(options.map(o => [o.value, o.label])),
    [options],
  );

  function toggle(value: string) {
    onChange(selected.includes(value) ? selected.filter(v => v !== value) : [...selected, value]);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex min-h-8 min-w-[160px] select-none items-center gap-1.5 rounded-md border border-border bg-background py-1 pl-2.5 pr-2 text-left',
            'hover:bg-zinc-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
            className,
          )}
        >
          <span className="shrink-0 text-[13px] text-muted-foreground">{label}</span>
          <span className="flex flex-1 flex-wrap gap-1">
            {selected.length === 0 ? (
              <span className="text-[13px] text-zinc-400">{placeholder}</span>
            ) : (
              selected.map(value => (
                <span
                  key={value}
                  className="inline-flex h-[22px] items-center gap-1 rounded-sm border border-border bg-secondary pl-1.5 pr-1 text-xs font-medium text-zinc-700"
                >
                  {labelByValue.get(value) ?? value}
                  <span
                    role="button"
                    tabIndex={-1}
                    aria-label={`Remove ${labelByValue.get(value) ?? value}`}
                    onClick={e => {
                      // Keep the popover from toggling when removing a tag.
                      e.stopPropagation();
                      onChange(selected.filter(v => v !== value));
                    }}
                    className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-[3px] text-zinc-400 hover:bg-border hover:text-foreground"
                  >
                    <X className="h-2.5 w-2.5" strokeWidth={3} />
                  </span>
                </span>
              ))
            )}
          </span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-zinc-400" />
        </button>
      </PopoverTrigger>

      <PopoverContent className="w-[280px] p-0">
        <Command shouldFilter={showSearch}>
          {showSearch && <CommandInput placeholder={searchPlaceholder ?? `Search ${label.toLowerCase()}…`} />}
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            {options.map(option => {
              const on = selected.includes(option.value);
              return (
                <CommandItem key={option.value} value={option.label} onSelect={() => toggle(option.value)}>
                  <span
                    className={cn(
                      'flex h-[15px] w-[15px] shrink-0 items-center justify-center rounded-[3px] border',
                      on ? 'border-primary bg-primary' : 'border-zinc-300 bg-background',
                    )}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={3.5}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className={cn('h-2.5 w-2.5 text-primary-foreground', on ? 'opacity-100' : 'opacity-0')}
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </span>
                  <span className="truncate">{option.label}</span>
                </CommandItem>
              );
            })}
          </CommandList>
          <div className="flex h-[34px] border-t border-secondary">
            <button
              type="button"
              onClick={() => onChange([])}
              className="flex-1 text-xs text-zinc-600 hover:bg-accent"
            >
              Clear
            </button>
            <div className="w-px bg-secondary" />
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="flex-1 text-xs text-zinc-600 hover:bg-accent"
            >
              Close
            </button>
          </div>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
